<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import TabBar from "@/components/TabBar.vue";
import { getPendingInvites } from "@/api/chat";
import { ChatPlugin } from "@/plugins/chat";
import { Capacitor } from "@capacitor/core";
import UpdateDialog from "@/components/UpdateDialog.vue";
import PrivacyConsentDialog from "@/components/PrivacyConsentDialog.vue";
import { hasPrivacyConsented, setPrivacyConsented } from "@/utils/privacyConsent";
import { versionCheckState, checkAppVersion } from "@/utils/versionCheck";
import { websocketService, type WebSocketCallbacks } from "@/services/websocket";

const route = useRoute();
const router = useRouter();
const showTabBar = computed(() => route.meta.showTabBar ?? false);

const userStore = useUserStore();
const isInitComplete = ref(false);

// 隐私政策授权状态（应用市场审核要求：首次打开必须先弹隐私政策弹窗）
const privacyAgreed = ref(hasPrivacyConsented());

// 协议/政策等 corp 页面在未同意前也要可渲染（弹窗内链接跳转查看用）
const isCorpRoute = computed(() => route.path.startsWith('/corp/'));

// ====================== RTC 来电检测逻辑 ======================
/**
 * 轮询间隔（毫秒）
 */
const POLL_INTERVAL = 3000; // 3秒轮询一次

/**
 * 轮询定时器
 */
let pollTimer = null;

/**
 * 轮询是否已停止
 */
let isPollingStopped = false;

/**
 * 当前正在处理的邀请ID（避免重复弹窗）
 */
let currentInviteId = null;

/**
 * 检查待处理的通话邀请
 */
const redirectAfterPayment = (payReturn) => {
  const path = sessionStorage.getItem('pendingPaymentRedirectPath');
  const orderNo = sessionStorage.getItem('pendingPaymentOrderNo');
  sessionStorage.removeItem('pendingPaymentRedirectPath');
  sessionStorage.removeItem('pendingPaymentOrderNo');
  if (path) {
    router.replace({ path, query: orderNo ? { orderNo, payReturn } : { payReturn } });
  } else {
    router.replace({ path: '/order/list', query: { payReturn } });
  }
};

const handlePendingPaymentRedirect = () => {
  if (document.visibilityState === 'hidden') {
    if (localStorage.getItem('pendingAlipayPaymentRedirect') === '1') {
      localStorage.setItem('pendingAlipayPaymentBackgrounded', '1');
    }
    return false;
  }

  if (sessionStorage.getItem('pendingWechatPaymentRedirect') === '1') {
    console.log('💳 检测到待处理微信支付跳转');
    sessionStorage.removeItem('pendingWechatPaymentRedirect');
    redirectAfterPayment('wechat');
    return true;
  }

  if (
    localStorage.getItem('pendingAlipayPaymentRedirect') === '1' &&
    localStorage.getItem('pendingAlipayPaymentBackgrounded') === '1'
  ) {
    console.log('💳 检测到待处理支付宝支付跳转');
    localStorage.removeItem('pendingAlipayPaymentRedirect');
    localStorage.removeItem('pendingAlipayPaymentBackgrounded');
    redirectAfterPayment('alipay');
    return true;
  }

  return false;
};

const checkPendingInvites = async () => {
  // 如果轮询已停止，不再执行
  if (isPollingStopped) return;

  // 未登录，不执行
  const currentToken = userStore.token;
  if (!currentToken) return;

  try {
    // 发出请求前再次检查 token 是否仍然有效
    if (!userStore.token || isPollingStopped) return;

    const response = await getPendingInvites();
    const invites = Array.isArray(response) ? response : (response?.data || []);

    if (invites.length > 0) {
      const invite = invites[0];

      // 避免重复处理同一个邀请
      if (currentInviteId === invite.inviteId) return;

      // 调用原生来电弹窗
      if (Capacitor.isNativePlatform()) {
        try {
          await ChatPlugin.showIncomingCall({
            inviteId: String(invite.inviteId),
            roomId: invite.roomId,
            callerName: invite.callerName || '健康师',
            callerAvatar: invite.callerAvatar || '',
          });

          // 标记已处理的邀请ID
          currentInviteId = invite.inviteId;
          console.log('📞 收到来电邀请:', invite.callerName);
        } catch (err) {
          console.error('❌ 来电弹窗失败：', err);
        }
      }
    } else {
      // 没有待处理的邀请，清空当前邀请ID（允许下次弹窗）
      currentInviteId = null;

      // 通知原生层关闭来电界面
      if (Capacitor.isNativePlatform()) {
        try {
          await ChatPlugin.dismissIncomingCall();
          console.log('✅ 已通知原生层关闭来电界面');
        } catch (err) {
          console.error('❌ 关闭来电界面失败：', err);
        }
      }
    }
  } catch (err) {
    console.error('❌ 检查待处理邀请失败：', err);
  }
};

/**
 * 启动轮询
 */
const startPolling = () => {
  if (pollTimer) return;

  isPollingStopped = false;
  console.log('📡 启动通话邀请轮询，间隔:', POLL_INTERVAL, 'ms');

  // 立即执行一次
  checkPendingInvites();

  // 定时轮询
  pollTimer = setInterval(() => {
    checkPendingInvites();
  }, POLL_INTERVAL);
};

/**
 * 停止轮询
 */
const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
    isPollingStopped = true;
    currentInviteId = null;
    console.log('🛑 停止通话邀请轮询');
  }
};

/**
 * WebSocket回调处理
 */
const handleCallStatusChange = (data) => {
  console.log('📞 [WebSocket回调] 收到通话状态变化');
  console.log('📞 [WebSocket回调] 状态码:', data.status);
  console.log('📞 [WebSocket回调] 状态文本:', data.statusText);
  console.log('📞 [WebSocket回调] 邀请ID:', data.inviteId);
  console.log('📞 [WebSocket回调] 房间ID:', data.roomId);
  console.log('📞 [WebSocket回调] 完整数据:', data);

  switch (data.status) {
    case 1: // ACCEPTED - 已接受
      console.log('✅ [WebSocket回调] 处理ACCEPTED状态 - 对方接受了通话');
      // 🔥 接受状态也要清空currentInviteId，允许后续来电弹窗
      if (currentInviteId === data.inviteId) {
        currentInviteId = null;
        console.log('🔄 [WebSocket回调] 已清空currentInviteId，允许后续来电弹窗');
      }
      break;
    case 2: // REJECTED - 已拒绝
      console.log('❌ [WebSocket回调] 处理REJECTED状态 - 对方拒绝了通话');
      console.log('🔔 [WebSocket回调] 准备关闭来电界面');
      // 关闭来电界面
      if (Capacitor.isNativePlatform()) {
        ChatPlugin.dismissIncomingCall()
          .then(() => {
            console.log('✅ [WebSocket回调] 来电界面已关闭');
            // 🔥 拒绝后清空currentInviteId，允许后续来电弹窗
            if (currentInviteId === data.inviteId) {
              currentInviteId = null;
              console.log('🔄 [WebSocket回调] 已清空currentInviteId，允许后续来电弹窗');
            }
          })
          .catch(err => console.error('❌ [WebSocket回调] 关闭来电界面失败:', err));
      }
      break;
    case 3: // CANCELLED - 已取消
      console.log('🚫 [WebSocket回调] 处理CANCELLED状态 - 对方取消了通话');
      console.log('🔔 [WebSocket回调] 准备关闭来电界面');
      // 关闭来电界面
      if (Capacitor.isNativePlatform()) {
        ChatPlugin.dismissIncomingCall()
          .then(() => {
            console.log('✅ [WebSocket回调] 来电界面已关闭');
            // 🔥 取消后清空currentInviteId，允许后续来电弹窗
            if (currentInviteId === data.inviteId) {
              currentInviteId = null;
              console.log('🔄 [WebSocket回调] 已清空currentInviteId，允许后续来电弹窗');
            }
          })
          .catch(err => console.error('❌ [WebSocket回调] 关闭来电界面失败:', err));
      }
      break;
    case 4: // EXPIRED - 已过期
      console.log('⏰ [WebSocket回调] 处理EXPIRED状态 - 通话邀请已过期');
      console.log('🔔 [WebSocket回调] 准备关闭来电界面');
      if (Capacitor.isNativePlatform()) {
        ChatPlugin.dismissIncomingCall()
          .then(() => {
            console.log('✅ [WebSocket回调] 来电界面已关闭');
            // 🔥 过期后清空currentInviteId，允许后续来电弹窗
            if (currentInviteId === data.inviteId) {
              currentInviteId = null;
              console.log('🔄 [WebSocket回调] 已清空currentInviteId，允许后续来电弹窗');
            }
          })
          .catch(err => console.error('❌ [WebSocket回调] 关闭来电界面失败:', err));
      }
      break;
    default:
      console.log('⚠️ [WebSocket回调] 未知状态:', data.status);
  }

  console.log('📞 [WebSocket回调] 状态处理完成');
};

const handleIncomingCall = (data) => {
  console.log('🔔 WebSocket收到来电邀请:', data);

  // 🚨 临时禁用WebSocket来电处理，避免与轮询冲突
  console.log('🚫 WebSocket来电处理已禁用，由轮询统一处理');
  return;

  // 🔥 避免重复处理同一个邀请（与轮询共享去重机制）
  if (currentInviteId === data.inviteId) {
    console.log('⚠️ WebSocket来电：已处理过该邀请，跳过');
    return;
  }

  // 调用原生来电弹窗
  if (Capacitor.isNativePlatform()) {
    try {
      ChatPlugin.showIncomingCall({
        inviteId: String(data.inviteId),
        roomId: data.roomId,
        callerName: data.callerName || '健康师',
        callerAvatar: data.callerAvatar || '',
      });

      // 标记已处理的邀请ID
      currentInviteId = data.inviteId;
      console.log('📞 WebSocket来电：已触发来电弹窗并标记');
    } catch (err) {
      console.error('❌ WebSocket来电弹窗失败：', err);
    }
  }
};

const handleNewMessage = (data) => {
  console.log('💬 WebSocket收到新消息:', data);
  // 可以触发未读消息数量更新等
};

const handleWebSocketConnected = () => {
  console.log('✅ WebSocket已连接，可以停止轮询');
  // WebSocket连接成功后，可以考虑停止轮询
  // stopPolling(); // 可选：停止轮询，完全依赖WebSocket
};

const handleWebSocketError = (error) => {
  console.error('❌ WebSocket错误，降级到轮询模式:', error);
  // WebSocket连接失败时，确保轮询在运行
  if (!pollTimer && userStore.token) {
    startPolling();
  }
};

onMounted(async () => {
  console.log('🚀 App.vue onMounted 开始执行')

  // 审核要求：用户在隐私弹窗点击"同意"之前，不发起任何网络请求、不初始化任何功能。
  // userStore.init 会校验 token（网络请求），RTC 轮询/WS/版本检测都在 initApp 内
  if (!privacyAgreed.value) {
    console.log('⏸️ 隐私政策未同意，暂缓 App 初始化，等待用户在隐私弹窗中确认')
    return
  }

  await initApp();
});

/**
 * App 初始化主流程：只能在用户同意隐私政策之后执行
 */
const initApp = async () => {
  console.log('🚀 开始执行 App 初始化（已同意隐私政策）')

  await userStore.init();
  isInitComplete.value = true;

  // 启动页逻辑已移至路由守卫处理

  // iOS 平台且已登录，启动轮询
  const platform = Capacitor.getPlatform();
  console.log('📱 当前平台:', platform);
  console.log('📱 Capacitor.isNativePlatform():', Capacitor.isNativePlatform());
  console.log('📱 用户 token:', userStore.token ? '有' : '无');

  // 🔍 诊断WebSocket启动条件
  console.log('🔍 [WebSocket诊断] 平台:', platform);
  console.log('🔍 [WebSocket诊断] 是否原生平台:', Capacitor.isNativePlatform());
  console.log('🔍 [WebSocket诊断] Token存在:', !!userStore.token);
  console.log('🔍 [WebSocket诊断] 是否Android平台:', platform === 'android');
  console.log('🔍 [WebSocket诊断] 是否iOS平台:', platform === 'ios');

  if (userStore.token) {
    console.log('✅ 启动通话邀请轮询');
    startPolling();

    // 🔥 连接WebSocket实时推送
    try {
      const token = localStorage.getItem('token') || userStore.token;
      console.log('🔍 [WebSocket诊断] 准备启动WebSocket连接 - Token存在:', !!token);
      if (token) {
        const wsCallbacks: WebSocketCallbacks = {
          onCallStatusChange: handleCallStatusChange,
          onIncomingCall: handleIncomingCall,
          onNewMessage: handleNewMessage,
          onConnected: handleWebSocketConnected,
          onError: handleWebSocketError
        };

        console.log('🔍 [WebSocket诊断] 开始调用websocketService.connect()');
        websocketService.connect(token, wsCallbacks);
        console.log('✅ WebSocket连接已启动');
      } else {
        console.log('❌ [WebSocket诊断] Token为空，无法启动WebSocket连接');
      }
    } catch (error) {
      console.error('❌ WebSocket连接失败，继续使用轮询模式:', error);
    }
  } else {
    console.log('⚠️ 不启动轮询 - platform:', platform, ', hasToken:', !!userStore.token);
    console.log('⚠️ [WebSocket诊断] WebSocket连接未启动 - 原因: Token不存在');
  }

  window.addEventListener('focus', handlePendingPaymentRedirect);
  window.addEventListener('pageshow', handlePendingPaymentRedirect);
  document.addEventListener('visibilitychange', handlePendingPaymentRedirect);

  // iOS 禁用双击滚动
  if (platform === 'ios') {
    let lastTouchEnd = 0;

    document.addEventListener('touchend', function (event) {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, { passive: false });
  }

  // 检查APP版本更新（原生平台）
  console.log('🔍 版本检测准备 - Capacitor.isNativePlatform():', Capacitor.isNativePlatform())

  if (Capacitor.isNativePlatform()) {
    console.log('✅ 原生平台，开始版本检测流程')

    // 获取当前版本号（从配置读取）
    const { getCurrentVersion } = await import('@/api/appVersion')
    const currentVersion = await getCurrentVersion()
    versionCheckState.value.currentVersion = currentVersion
    console.log('📱 当前APP版本:', currentVersion)

    // 延迟1秒检测，避免影响启动速度
    setTimeout(() => {
      console.log('⏰ 1秒后开始检测版本...')
      checkAppVersion('aroma');
    }, 1000);
  } else {
    console.log('🌐 非原生平台，跳过版本检测 - 请在真机或模拟器上测试')
  }

  console.log('✅ App 初始化执行完成')
};

/**
 * 隐私弹窗点击"同意"：记录授权状态并启动 App 初始化
 */
const handlePrivacyAgree = async () => {
  console.log('✅ 用户已同意隐私政策，开始 App 初始化')
  setPrivacyConsented();
  privacyAgreed.value = true;
  await initApp();
};

onUnmounted(() => {
  stopPolling();
  websocketService.disconnect(); // 断开WebSocket连接
  window.removeEventListener('focus', handlePendingPaymentRedirect);
  window.removeEventListener('pageshow', handlePendingPaymentRedirect);
  document.removeEventListener('visibilitychange', handlePendingPaymentRedirect);
});

// 监听用户登录状态变化，启动/停止轮询和WebSocket
watch(() => userStore.token, () => {
  // 直接从 localStorage 检查 token，如果为空就不轮询
  const hasToken = !!localStorage.getItem('token');
  if (hasToken) {
    startPolling();

    // 🔥 连接WebSocket
    const token = localStorage.getItem('token');
    if (token) {
      const wsCallbacks: WebSocketCallbacks = {
        onCallStatusChange: handleCallStatusChange,
        onIncomingCall: handleIncomingCall,
        onNewMessage: handleNewMessage,
        onConnected: handleWebSocketConnected,
        onError: handleWebSocketError
      };

      websocketService.connect(token, wsCallbacks);
    }
  } else {
    stopPolling();
    websocketService.disconnect();
  }
});

// 监听路由变化
watch(
  () => route.path,
  (newPath) => {
    const appContainer = document.getElementById("app");
    appContainer?.scrollTo(0, 0);

    // 版本更新弹窗控制：在推荐页显示卡片而非弹窗
    if (newPath === '/' || newPath === '/top') {
      // 进入推荐页，关闭弹窗状态（会显示卡片）
      if (versionCheckState.value.showUpdateDialog &&
          versionCheckState.value.versionInfo?.isForce !== 1) {
        versionCheckState.value.showUpdateDialog = false;
      }
    }
  }
);

// 监听版本检测状态变化，在推荐页时阻止弹窗显示
watch(
  () => versionCheckState.value.showUpdateDialog,
  (shouldShow) => {
    if (shouldShow && (route.path === '/' || route.path === '/top')) {
      // 在推荐页检测到更新，关闭弹窗状态（显示卡片）
      if (versionCheckState.value.versionInfo?.isForce !== 1) {
        versionCheckState.value.showUpdateDialog = false;
      }
    }
  }
);
</script>

<template>
  <router-view v-slot="{ Component }">
    <!-- 未同意隐私政策前不挂载任何页面（corp 协议页除外）：否则页面在弹窗下挂载会发出被拦截的
         请求，页面自身的错误处理会弹"获取数据失败"类提示；同意后 key 变化重挂载当前页重新拉数据 -->
    <component v-if="privacyAgreed || isCorpRoute" :is="Component" :key="privacyAgreed" />
  </router-view>
  <TabBar v-if="showTabBar && (privacyAgreed || isCorpRoute)" />

  <!-- 版本更新弹窗（推荐页不显示，改为卡片提示） -->
  <UpdateDialog
    v-if="route.path !== '/' && route.path !== '/top'"
    v-model:show="versionCheckState.showUpdateDialog"
    :version-info="versionCheckState.versionInfo || {}"
    :current-version="versionCheckState.currentVersion"
  />

  <!-- 首次启动隐私政策弹窗（审核要求：同意前不收集任何信息） -->
  <PrivacyConsentDialog :show="!privacyAgreed" @agree="handlePrivacyAgree" />
</template>

<style scoped>
</style>
